<script setup lang="ts">
import type { SidebarMenuItem } from "@/types/global"
import type { PropType, Ref } from "vue"
import LanguageSelect from "@/app/components/LanguageSelect.vue"
import BaseIcon from "@/app/components/ui/BaseIcon.vue"
import { useConfig } from "@/app/composables/useConfig"
import { useI18n } from "@/app/composables/useI18n"
import { useTooltip } from "@/app/composables/useTooltip"
import { useDepositFormStore } from "@/app/stores/depositFormStore"
import { useAuth } from "@/user-account/composables/useAuth"
import { storeToRefs } from "pinia"
import { computed, onBeforeUnmount, onMounted, ref } from "vue"
import { useBetStore } from "../../../../store"
import MoneyTransferCrypto from "../MyBets/Wallets/MoneyTransferCrypto.vue"
import SidebarSection from "./SidebarSection.vue"

const props = defineProps({
  categories: {
    type: Array as PropType<
      {
        id: number | string
        name: string
        slug: string
        image?: { url: string } | null
        image_url?: string
      }[]
    >,
    default: () => [],
  },
})

const buyCryptoIcons = [
  { name: "buy-crypto-methods1", size: "40", color: "var(--be-primary-btn-label)" },
  { name: "buy-crypto-methods2", size: null, color: "var(--be-primary-btn-label)" },
  { name: "buy-crypto-methods3", size: "40", color: "var(--be-primary-btn-label)" },
  { name: "buy-crypto-methods4", size: "40", color: "var(--be-primary-btn-label)" },
]

const { isDrawerOpen, appDivisions, appDivision } = useConfig()
const { tooltipVisible, tooltipPosition, updateTooltipPosition, getTooltipText } = useTooltip()
const { isAuthenticated } = useAuth()
const { t } = useI18n()

const language = ref(false)

const rootItems: SidebarMenuItem[] = []
const store = useBetStore()
const { formData } = storeToRefs(useDepositFormStore())
const baseItems = computed(() => [
  {
    id: -101,
    title: t("sportsbook.mybets"),
    icon: "bets",
    route: { name: "" },
    click: true,
    imageUrl: null,
    handleClick: () => {
      if (isAuthenticated()) {
        store.handleShowBetModal()
        store.setSection("bet")
      }
      else {
        store.handleShowLogin()
      }
    },
  },
])
const showModal = ref(false)

const brandName = import.meta.env.VITE_APP_BRAND
const swipeLuxPublicKey = import.meta.env.VITE_APP_SWIPELUX_PUBLIC_KEY
const swipeLuxUrl = import.meta.env.VITE_APP_SWIPELUX_URL
const swipeLuxWidgetUrl = import.meta.env.VITE_APP_SWIPELUX_WIDGET_URL

const supportItems = computed(() => {
  const items = [
    {
      id: -101,
      title: t("general.live_support"),
      icon: "live",
      route: { name: "" },
      click: true,
      imageUrl: null,
      handleClick: () => store.handleShowChatModal(),
    },
  ]

  if (brandName !== "betevet") {
    items.push({
      id: 103,
      title: t("general.language"),
      icon: "globe",
      route: { name: "" },
      click: true,
      imageUrl: null,
      handleClick: () => (language.value = !language.value),
    })
  }

  return items
})

if (appDivisions.value.includes("casino")) {
  rootItems.push({
    id: -102,
    title: t("general.casino"),
    icon: "lobby",
    imageUrl: null,
    route: { name: "casino" },
    click: false,
    handleClick: undefined,
  })
}

const casinoItems: Ref<SidebarMenuItem[]> = computed(() => {
  return props.categories.map((category) => {
    return {
      id: Number(category.id),
      title: category.name,
      icon: null,
      route: {
        name: "casino.category",
        params: { slug: category.slug },
      },
      imageUrl: category.image_url ?? null,
      click: false,
      handleClick: undefined,
    }
  })
})

const screenWidth = ref(window.innerWidth)
const isMobile = computed(() => {
  return store.getScreenWidth < 512
})

function handleResize() {
  store.setScreenWidth(window.innerWidth)
  screenWidth.value = window.innerWidth
  if (!isMobile.value && isDrawerOpen.value) {
    isDrawerOpen.value = true
  }
  else {
    isDrawerOpen.value = false
  }
}

function handleShowDrawer() {
  if (isAuthenticated()) {
    store.handleShowBetModal(false)
  }

  if (!isMobile.value) {
    isDrawerOpen.value = !isDrawerOpen.value
  }

  if (isMobile.value) {
    window.document.body.style.overflow = isDrawerOpen.value ? "hidden" : "auto"
  }
}

function handleClickOutside(event: MouseEvent) {
  if (isMobile.value && isDrawerOpen.value) {
    const sidebarEl = document.querySelector(".side-bar-col")
    if (sidebarEl && !sidebarEl.contains(event.target as Node)) {
      handleShowDrawer()
    }
  }
}

onMounted(() => {
  window.addEventListener("resize", handleResize)
  document.addEventListener("mousedown", handleClickOutside)
  handleResize()
})

onBeforeUnmount(() => {
  window.removeEventListener("resize", handleResize)
  document.removeEventListener("mousedown", handleClickOutside)

  document.body.style.overflow = "auto"
})

function handleDepostBet() {
  if (isAuthenticated()) {
    store.handleShowBetModal()
    store.setSection("wallet")
    store.setWalletTab("deposit")
  }
  else {
    store.handleShowLogin()
  }
}

function handleOpenModal() {
  if (isAuthenticated()) {
    showModal.value = true
  }
  else {
    store.handleShowLogin()
  }
}

async function handleBuyCrypto() {
  const settings = {
    widgetUrl: swipeLuxWidgetUrl,
    apiKey: swipeLuxPublicKey,
    availableReceiveCurrency: formData.value?.currency || "",
    defaultValues: {
      email: { value: formData.value?.email, editable: true },
      phone: { value: formData.value?.phone, editable: true },
      targetAddress: { value: formData.value?.cryptoAddress, editable: false },
      fiatAmount: formData.value?.depositAmount,
    },
  }

  const specificSettingsParam = encodeURIComponent(JSON.stringify(settings))
  const url = `${swipeLuxUrl}/?specificSettings=${specificSettingsParam}`

  if (url) {
    showModal.value = false
    window.open(url, "_blank")
  }
}

function handleCloseModal() {
  showModal.value = false
}
</script>

<template>
  <v-card style="z-index: 11">
    <v-layout>
      <v-navigation-drawer
        v-model="isDrawerOpen"
        :expand-on-hover="false"
        :style="`top: 60px; border: none; width:${
          isDrawerOpen ? '' : '270px'
        }; transform:translateX(${
          isDrawerOpen ? '0%' : screenWidth <= 568 && !isDrawerOpen ? '-200%' : null
        })`"
        class="side-bar-col"
        :rail="screenWidth >= 568 ? isDrawerOpen : false"
        data-testid="app-sidebar"
      >
        <div v-if="language && brandName !== 'betevet'" class="modal-overlay" @click="() => (language = false)">
          <LanguageSelect
            :class="` ${!isDrawerOpen ? 'language-selector' : 'shrink-language-selector'}`"
            @close="() => (language = false)"
          />
        </div>
        <div class="wrapper" style="margin-bottom: 150px">
          <SidebarSection
            :menu-items="baseItems"
            :show-separator="screenWidth >= 568 ? !isDrawerOpen : true"
          />
          <SidebarSection
            v-if="appDivision === 'casino'"
            :menu-items="casinoItems"
            :title="t('general.casino')"
            :game-heading="true"
            :show-separator="screenWidth >= 568 ? !isDrawerOpen : true"
          />

          <SidebarSection
            :menu-items="supportItems"
            :title="t('general.virtualGames')"
            :show-separator="screenWidth >= 568 ? !isDrawerOpen : true"
          />
          <div class="deposit-btn" @click="handleDepostBet">
            <div
              :class="`${isDrawerOpen ? 'ml-0' : 'ml-_11'}`"
              @mouseenter="
                isDrawerOpen
                  && (tooltipVisible = true)
                  && updateTooltipPosition($event)
              "
              @mouseleave="isDrawerOpen && (tooltipVisible = false)"
            >
              <BaseIcon name="deposit" size="18" color="var(--be-primary-btn-label)" />
            </div>
            <div v-if="screenWidth >= 568 ? !isDrawerOpen : true">
              {{ t("finance.deposit") }}
            </div>
          </div>
          <div class="buy-crypto-btn" @click="handleOpenModal">
            <div
              :class="`${isDrawerOpen ? 'ml-0' : 'ml-_11'}`"
              @mouseenter="
                isDrawerOpen
                  && (tooltipVisible = true)
                  && updateTooltipPosition($event)
              "
              @mouseleave="isDrawerOpen && (tooltipVisible = false)"
            >
              <BaseIcon name="buy-crypto" size="18" color="var(--be-primary-btn-label)" />
            </div>
            <div v-if="screenWidth >= 568 ? !isDrawerOpen : true">
              {{ t("finance.buy_crypto") }}
            </div>
          </div>
          <div
            class="buy-crypto-availableMethods" :class="{
              hidden: screenWidth >= 568 && isDrawerOpen,
            }"
          >
            <BaseIcon
              v-for="(icon, index) in buyCryptoIcons"
              :key="index"
              :name="icon.name"
              :size="icon.size"
              :color="icon.color"
            />
          </div>
        </div>
      </v-navigation-drawer>

      <!-- Backdrop overlay for mobile when sidebar is open -->
      <div
        v-if="isMobile && !isDrawerOpen"
        class="sidebar-backdrop"
        @click="handleShowDrawer"
      />
    </v-layout>
  </v-card>

  <Teleport to="body">
    <div
      v-if="tooltipVisible"
      class="tooltip-teleport"
      :style="`
        position: fixed;
        left: ${tooltipPosition.x + 10}px;
        top: ${tooltipPosition.y}px;
        z-index: 9999;
        transform: translateY(-50%);
        background-color: white;
        color: #000;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 14px;
        white-space: nowrap;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      `"
    >
      {{ getTooltipText }}
    </div>
  </Teleport>

  <Transition name="modal">
    <div v-if="showModal" class="modal-mask-bonus">
      <div class="modal-container">
        <div class="modal-detail">
          <div class="close-btn" @click="handleCloseModal">
            <div>{{ t("general.close") }}</div>
            <img src="/assets/images/close-modal-icon.svg" alt="">
          </div>

          <div class="modal-body">
            <div class="status-heading">
              {{ t("finance.buy_crypto") }}
            </div>
            <MoneyTransferCrypto />

            <button class="btn-g" @click="handleBuyCrypto">
              {{ t("finance.buy_crypto") }}
            </button>
          </div>

          <div
            class="buy-crypto-availableMethods withinModal" :class="{
              column: screenWidth >= 568 && isDrawerOpen,
            }"
          >
            <BaseIcon
              v-for="(icon, index) in buyCryptoIcons"
              :key="index"
              :name="icon.name"
              :size="icon.size"
              :color="icon.color"
            />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/thin-scrollbar.scss";

.buy-crypto-availableMethods {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  align-items: center;
  margin-top: 10px;
  padding: 0 30px;

  &.hidden {
    display: none;
  }

  &.withinModal {
    display: flex;
    justify-content: center;
    gap: 20px;

    > div,img {
      margin: 0;
    }
  }

  >div,img {
    width: 40px;
    height: auto;
  }
}

.btn-g {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--be-primary-btn-label);
  border: none;
  height: 42px;
  width: 100%;
  border-radius: 4px;
  background: var(--Linear, linear-gradient(90deg, #3886ec 0%, #544fe5 100%));
  margin: 12px 0px;

  &:hover {
    opacity: 0.8;
  }
}

.expand-btn {
  display: flex;
  justify-content: flex-end;
  margin: 10px;
  margin-bottom: 0;
  background: var(--be-small-card-bg);
  border-radius: 8px;
  padding: 5px;
  cursor: pointer;
  margin-bottom: 10px;
}

.closer-container {
  display: flex;
  justify-content: flex-end;
  margin: 10px;
  margin-bottom: 0;
}

.closer {
  background: var(--be-small-card-bg);
  border-radius: 8px;
  padding: 5px;
  cursor: pointer;

}

.closer:hover {
  background: var(--be-divider);
}

.modal-overlay {
  position: fixed;
  top: -60px;
  left: 0;
  right: 0;
  bottom: 0;
  background: transparent;
  width: 1000vh;
  height: 100vh;
  z-index: 99;
}

.side-bar-col {
  background: var(--be-big-card-bg);
  color: var(--be-primary-text-color);

}

.deposit-btn,
.buy-crypto-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  gap: 8px;
  height: 40px;
  background: var(--be-primary-btn);
  color: var(--be-primary-btn-label);
  margin: 10px;
  font-size: 14px;
  font-family: Rubik-med;
  opacity: 0.9;
  cursor: pointer;
  position: relative;
  z-index: 1;
}
.deposit-btn:hover,
.buy-crypto-btn:hover {
  opacity: 1;
}
.tooltip-teleport {
  position: relative;
}

.tooltip-teleport::before {
  content: "";
  position: absolute;
  top: 50%;
  right: 100%;
  margin-top: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent white transparent transparent;
}

.ml-0 {
  margin-left: 0px;
}
.ml-_11 {
  margin-left: -11px;
}

.language-selector {
  flex-shrink: 0;
  margin: 0 10px 0 0;
  position: absolute;
  left: 271px;
  top: 150px;
  width: 186px;

  background: var(--be-small-card-bg);
  overflow: auto;
}

.shrink-language-selector {
  left: 55px;
  flex-shrink: 0;
  margin: 0 10px 0 0;
  position: absolute;
  top: 150px;
  width: 186px;

  background: var(--be-small-card-bg);
  overflow: auto;
}
.sidenav {
  margin-top: 67px;
  padding: 20px;
  width: 90%;
  max-width: 300px;
  position: fixed;
  overflow-y: scroll;
  top: 0;
  left: 0;
  height: calc(100% - 67px);
  background: var(--be-bg);
  pointer-events: auto;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  transform: translateX(-100%);
  transition: transform 0.3s;
  will-change: transform;
  z-index: 99;
  &.opened {
    transform: translateX(0);
  }
}
@media screen and (min-width: 500px) {
  .wrapper {
    position: absolute;
    width: 100%;
    max-height: calc(100vh - 60px); // Adjust the height as needed
  overflow-y: auto;
  overflow-x: hidden;
  }

}
@media screen and (max-width:550px) {
  .modal-overlay{
    left: 100px;
  }
}

.expand-btn-inner {
  background: var(--be-small-card-bg);
  border-radius: 8px;
  padding: 5px;
  cursor: pointer;
  margin: 10px;
}

.expand-btn-inner:hover {
  background: var(--be-divider);
}

.modal-mask-bonus {
  position: fixed;
  z-index: 9998;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(13, 12, 44, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  transition: opacity 0.3s ease;

.close-btn {
  color: white;
  position: absolute;
  top: -32px;
  right: 3px;
  display: flex;
  gap: 0 5px;
  cursor: pointer;
}

.modal-container {
  width: 600px;
  margin: auto;
  height: 100%;
  overflow: auto;
  transition: all 0.3s ease;
  position: relative;
  display: flex;
}

.modal-detail {
  background: var(--be-bg);
  padding: 20px 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.33);
  margin-top: 13px;
  border-radius: 20px;
  margin: 100px 0 auto 0;
  position: relative;
  width: 100%;
}

.status-heading {
  margin-bottom: 10px;
  font-family: 'Poppins-bold';
  font-size: 24px;
  margin-bottom: 20px;
}

.modal-body {
  border-radius: 8px;
  padding: 10px 10px 0px 10px;
}

.modal-enter-from {
  opacity: 0;
}

.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  -webkit-transform: scale(1.1);
  transform: scale(1.1);
}
}

@media screen and (max-width: 500px) {
  .modal-mask-bonus {
    top: 2;
  }

  .modal-detail {
    margin: 0px;
    padding: 0px;
  }

  .modal-container {
    padding: 0 16px 16px 16px;
  }
}
</style>
